import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WhiteLabelConfig } from 'src/store/models/WhiteLabel';
import { localStorageHelper, StoragePrefix } from 'src/utils/localStorage';

import { ErrorAction, GlobalFile, LoadingAction } from '../store/models/Action';
import { Modal } from '../store/models/Modal';
import { Snackbar } from '../store/models/Snackbar';

export interface GlobalState {
  modal: Modal;
  snackbars: Snackbar[];
  globalLoading: boolean;
  loadingActions: LoadingAction[];
  errorActions: ErrorAction[];
  rememberMe: boolean;
  files: GlobalFile[];
  dynamicSettings: {
    whiteLabelsConfig: WhiteLabelConfig[];
  };
}

const initialState: GlobalState = {
  modal: null,
  snackbars: [],
  globalLoading: false,
  loadingActions: [],
  errorActions: [],
  rememberMe: localStorageHelper?.get(StoragePrefix.REMEMBER_ME) || false,
  files: [],
  dynamicSettings: {
    whiteLabelsConfig: [],
  },
};

const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    setRememberMe(state, action: PayloadAction<boolean>) {
      state.rememberMe = action.payload;
    },
    setGlobalLoading(state, action: PayloadAction<boolean>) {
      state.globalLoading = action.payload;
    },
    addSnackbar(state, action: PayloadAction<Snackbar>) {
      state.snackbars = [...state.snackbars, action.payload].slice(-3);
    },
    removeSnackbar(state, action: PayloadAction<string>) {
      state.snackbars = state.snackbars.filter(
        (snackbar) => snackbar.id !== action.payload
      );
    },
    removeSnackbarWithAction(state, action: PayloadAction<string>) {
      state.snackbars = state.snackbars.filter(
        (snackbar) => snackbar.actionId !== action.payload
      );
    },
    addModal(state, action: PayloadAction<Modal>) {
      state.modal = action.payload;
    },
    removeModal(state) {
      state.modal = null;
    },
    startLoading(state, action: PayloadAction<LoadingAction>) {
      state.loadingActions.push(action.payload);
      state.errorActions = state.errorActions.filter((savedAction) =>
        filterAction(savedAction, action)
      );
    },
    stopLoading(state, action: PayloadAction<LoadingAction>) {
      state.loadingActions = state.loadingActions.filter((savedAction) =>
        filterAction(savedAction, action)
      );
    },
    addError(state, action: PayloadAction<ErrorAction>) {
      state.errorActions.push(action.payload);
    },
    removePropertyError(
      state,
      action: PayloadAction<{ action: LoadingAction; name: string }>
    ) {
      const errorAction = state.errorActions.find(
        (e) =>
          e.actionType === action.payload?.action?.actionType &&
          e.id === action.payload?.action?.id
      );
      if (Array.isArray(errorAction?.error)) {
        errorAction.error = errorAction.error?.map((e) =>
          e.startsWith(action.payload?.name) ? '' : e
        );
      }
    },
    removeError(state, action: PayloadAction<LoadingAction>) {
      state.errorActions = state.errorActions.filter((savedAction) =>
        filterAction(savedAction, action)
      );
    },
    getFileUrl(state, action: PayloadAction<{ file: File; id: string }>) {
      return undefined;
    },
    addFile(state, action: PayloadAction<GlobalFile>) {
      state.files.push(action.payload);
    },
    removeFile(state, action: PayloadAction<GlobalFile>) {
      // revoke file URL
      state.files.forEach((f) => {
        if (
          f.id === action.payload?.id &&
          (action.payload?.url ? action.payload?.url === f.url : true)
        ) {
          URL.revokeObjectURL(f.url);
        }
      });
      state.files = state.files.filter((f) => filterFile(f, action));
    },
    getDynamicSettings() {
      return undefined;
    },
    setDynamicSettings(
      state,
      action: PayloadAction<{ whiteLabelsConfig: WhiteLabelConfig[] }>
    ) {
      state.dynamicSettings.whiteLabelsConfig =
        action.payload.whiteLabelsConfig;
    },
    editDynamicSettings(
      state,
      action: PayloadAction<{ whiteLabelsConfig: WhiteLabelConfig[] }>
    ) {
      return undefined;
    },
  },
});

const filterAction = (
  currAction: LoadingAction,
  action: PayloadAction<LoadingAction>
) => {
  if (currAction.actionType !== action.payload?.actionType) {
    return true;
  } else return currAction.id !== action.payload?.id;
};

const filterFile = (
  currFile: GlobalFile,
  action: PayloadAction<GlobalFile>
) => {
  if (currFile?.id !== action.payload?.id) {
    return true;
  }
  return action.payload?.url ? currFile?.url !== action.payload?.url : false;
};

export const {
  setRememberMe,
  addSnackbar,
  removeSnackbar,
  removeSnackbarWithAction,
  removeModal,
  addModal,
  setGlobalLoading,
  addError,
  removeError,
  stopLoading,
  startLoading,
  getFileUrl,
  addFile,
  removeFile,
  removePropertyError,
  getDynamicSettings,
  setDynamicSettings,
  editDynamicSettings,
} = globalSlice.actions;
export default globalSlice.reducer;
